'use client';

import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { PigeonLogo } from '@/components/branding/PigeonLogo';
import { Button } from '@/components/ui/Button';
import { Typography } from '@/components/ui/Typography';
import { getLandingContactDetails } from '@/features/landing/contact';
import { getLandingHeroRotatorPairs } from '@/features/landing/heroRotator';
import { useI18n } from '@/hooks/i18n/useI18n';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { LandingEarthPeek } from '../LandingEarthPeek/LandingEarthPeek';
import { LandingHeroRotator } from '../LandingHeroRotator/LandingHeroRotator';
import { Stars } from '../Stars';
import styles from './LandingPage.module.scss';

const splitHeroTitle = (title: string) => {
    const normalized = title.trim();
    const words = normalized.split(/\s+/u).filter(Boolean);

    if (words.length >= 3) {
        const midpoint = Math.ceil(words.length / 2);

        return {
            lead: words.slice(0, midpoint).join(' '),
            accent: words.slice(midpoint).join(' '),
        };
    }

    const dividerIndex = normalized.indexOf(' ');

    if (dividerIndex <= 0 || dividerIndex >= normalized.length - 1) {
        return {
            lead: normalized,
            accent: '',
        };
    }

    return {
        lead: normalized.slice(0, dividerIndex),
        accent: normalized.slice(dividerIndex + 1),
    };
};

const landingContactDetails = getLandingContactDetails();

const LandingPageComponent = ({ downloadUnavailable = false }: { downloadUnavailable?: boolean }) => {
    const { landingContent: content } = useI18n();
    const { lead: heroTitleLead, accent: heroTitleAccent } = useMemo(() => splitHeroTitle(content.title), [content.title]);
    const heroRotatorSubjects = useMemo(
        () => content.heroRotator?.subjects.map((subject) => subject.trim()).filter(Boolean) ?? [],
        [content.heroRotator]
    );
    const heroRotatorAudiences = useMemo(
        () => content.heroRotator?.audiences.map((audience) => audience.trim()).filter(Boolean) ?? [],
        [content.heroRotator]
    );
    const heroRotatorPairs = useMemo(
        () => getLandingHeroRotatorPairs(heroRotatorSubjects, heroRotatorAudiences),
        [heroRotatorAudiences, heroRotatorSubjects]
    );
    const heroRotatorConnector = content.heroRotator?.connector.trim() ?? '';
    const hasHeroRotator = heroRotatorPairs.length > 0 && heroRotatorConnector.length > 0;
    const downloadNotice = content.downloadUnavailableNotice;
    const structuredData = useMemo(
        () => ({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Chattenger',
            alternateName: ['Чаттенджер', 'Чатенджер'],
            url: 'https://chattenger.ru/',
            logo: 'https://chattenger.ru/branding/pigeon-logo.png',
            description: content.description,
            sameAs: ['https://chattenger.ru/'],
        }),
        [content.description]
    );

    return (
        <main className={clsx(styles.root, downloadUnavailable && styles.rootWithNotice)} data-testid="components-landing-landing-page-root">
            <Stars />
            <LandingEarthPeek />
            <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            <nav
                aria-label={content.contactDetailsLinkLabel}
                className={clsx(styles.contactShortcut, downloadUnavailable && styles.contactShortcutWithNotice)}
                data-testid="components-landing-landing-page-contact-shortcut"
            >
                <Button
                    className={styles.contactShortcutLink}
                    data-testid="components-landing-landing-page-contact-link"
                    href={landingContactDetails.pagePath}
                    size="compact"
                    variant="ghost"
                >
                    {content.contactPageTitle}
                </Button>
            </nav>

            {downloadUnavailable ? (
                <section aria-live="polite" className={styles.downloadNotice} data-testid="components-landing-landing-page-download-notice">
                    <Typography
                        as="strong"
                        className={styles.downloadNoticeTitle}
                        data-testid="components-landing-landing-page-download-notice-title"
                        size="title-sm"
                        weight="semibold"
                    >
                        {downloadNotice.title}
                    </Typography>
                    <Typography
                        className={styles.downloadNoticeCopy}
                        data-testid="components-landing-landing-page-download-notice-copy"
                        size="body"
                        tone="muted"
                    >
                        {downloadNotice.description}
                    </Typography>
                </section>
            ) : null}

            <section className={styles.hero} data-testid="components-landing-landing-page-hero">
                <article className={styles.heroCopy} data-testid="components-landing-landing-page-hero-copy">
                    <div className={styles.heroVisual} data-testid="components-landing-landing-page-hero-visual">
                        <PigeonLogo className={styles.heroLogo} dataTestId="components-landing-landing-page-hero-logo" decorative variant="hero" />
                    </div>
                    {content.kicker && (
                        <Typography
                            as="span"
                            className={styles.heroEyebrow}
                            data-testid="components-landing-landing-page-hero-eyebrow"
                            size="eyebrow"
                            tone="accent"
                            weight="bold"
                        >
                            {content.kicker}
                        </Typography>
                    )}
                    <Typography aria-label={content.title} as="h1" className={styles.heroTitle} size="display" weight="bold">
                        {hasHeroRotator ? (
                            <LandingHeroRotator connector={heroRotatorConnector} pairs={heroRotatorPairs} />
                        ) : (
                            <>
                                <span className={styles.heroTitleLead} data-testid="components-landing-landing-page-hero-title-lead">
                                    {heroTitleLead}
                                </span>
                                {heroTitleAccent ? (
                                    <span className={styles.heroTitleAccent} data-testid="components-landing-landing-page-hero-title-accent">
                                        {heroTitleAccent}
                                    </span>
                                ) : null}
                            </>
                        )}
                    </Typography>
                    {hasHeroRotator ? null : (
                        <Typography
                            align="center"
                            className={styles.heroPromise}
                            data-testid="components-landing-landing-page-hero-promise"
                            size="title-lg"
                            weight="medium"
                            balance
                        >
                            {content.promise}
                        </Typography>
                    )}
                    <Typography
                        aria-label={content.description}
                        className={styles.heroDescription}
                        data-testid="components-landing-landing-page-hero-description"
                        size="body-lg"
                        tone="muted"
                        align="center"
                    >
                        <span className={styles.heroDescriptionLine} data-testid="components-landing-landing-page-hero-description-line-1">
                            {content.description}
                        </span>
                    </Typography>
                    <div className={styles.heroActions} data-testid="components-landing-landing-page-hero-actions">
                        <Button
                            className={styles.heroActionPrimary}
                            data-testid="components-landing-landing-page-hero-download-link"
                            href="/download"
                            navigationMode="document"
                        >
                            <DownloadRoundedIcon aria-hidden="true" className={styles.heroActionIcon} fontSize="inherit" />
                            <span>{content.appDownloadCtaLabel}</span>
                        </Button>
                        <div className={styles.heroSignInStack} data-testid="components-landing-landing-page-hero-sign-in-stack">
                            <Typography
                                as="p"
                                className={styles.heroSignInPrompt}
                                data-testid="components-landing-landing-page-hero-sign-in-prompt"
                                size="body"
                                weight="semibold"
                            >
                                {content.signInPrompt}
                            </Typography>
                            <Button
                                className={styles.heroActionSecondary}
                                data-testid="components-landing-landing-page-hero-login-link"
                                href="/login"
                                variant="secondary"
                            >
                                {content.signInCtaLabel}
                            </Button>
                        </div>
                    </div>
                </article>
            </section>
        </main>
    );
};

LandingPageComponent.displayName = 'LandingPage';

export const LandingPage = memo(LandingPageComponent);
